import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Icon } from "@iconify/react";
import MetadataTree from "./MetadataTree";
import {
  countEntries,
  formatMetadata,
  getValueKind,
  isPlainObject,
  parseMetadata,
  serializeMetadata,
} from "../../utils/metadata";

const Toolbar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ModeButton = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  color: var(--main-font-color);
  background-color: var(--main-bg);
  border: 1px solid var(--main-border-color);

  &.active {
    color: var(--button-font-color);
    background-color: var(--button-bg);
    border-color: var(--button-bg);
  }
`;

const PlainButton = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 14px;
  border-radius: 6px;
  cursor: pointer;
  color: var(--main-font-color);
  border: 1px dashed var(--main-border-color);

  &:hover {
    background-color: var(--content-item-bg);
  }
`;

const JsonBox = styled.textarea`
  width: 100%;
  min-height: 340px;
  padding: 12px;
  font-family: monospace;
  font-size: 13px;
  line-height: 1.5;
  color: var(--main-font-color);
  background-color: var(--main-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  resize: vertical;
  box-sizing: border-box;
  outline: none;

  &:focus {
    border-color: var(--button-bg);
  }
`;

const ErrorBanner = styled.div`
  padding: 10px 12px;
  font-size: 14px;
  border-radius: 6px;
  border: 1px solid #c0392b;
  background-color: rgba(192, 57, 43, 0.1);
  color: #c0392b;
`;

const Hint = styled.div`
  font-size: 13px;
  color: #a0a6b1;
`;

const Spacer = styled.div`
  flex: 1;
`;

/**
 * Editor for the dataset's metadata string.
 *
 * `value` is the raw string as stored on the dataset; `onChange` receives the next raw string,
 * so the caller can drop it straight into the record that the Save Changes button persists.
 */
export const MetadataEditor = ({ value, onChange = () => { } }) => {
  const parsed = useMemo(() => parseMetadata(value), [value]);

  const rootEditable = parsed.ok && (isPlainObject(parsed.value) || Array.isArray(parsed.value));

  const [mode, setMode] = useState(rootEditable ? "structured" : "json");
  const [jsonText, setJsonText] = useState(() => formatMetadata(parsed.ok ? parsed.value : {}));
  const [jsonError, setJsonError] = useState(null);

  // Seed the JSON box when switching into it (not on every keystroke, which would reformat
  // the text under the caret).
  useEffect(() => {
    if (mode === "json" && parsed.ok) {
      setJsonText(formatMetadata(parsed.value));
      setJsonError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const commitJson = (text) => {
    setJsonText(text);

    if (text.trim() === "") {
      setJsonError("Enter JSON, or reset to an empty object.");
      return;
    }

    try {
      const next = JSON.parse(text);
      setJsonError(null);
      onChange(serializeMetadata(next));
    } catch (error) {
      setJsonError(error.message);
    }
  };

  if (!parsed.ok) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <ErrorBanner>
          This metadata is not valid JSON ({parsed.error}). Fix it below, or reset it.
        </ErrorBanner>
        <JsonBox
          value={value ?? ""}
          spellCheck={false}
          onChange={(event) => onChange(event.target.value)}
        />
        <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
          <PlainButton onClick={() => onChange(serializeMetadata({}))}>
            <Icon icon="mdi:restore" /> Reset to empty object
          </PlainButton>
        </div>
      </div>
    );
  }

  const entryCount = countEntries(parsed.value);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <Toolbar>
        <ModeButton
          className={mode === "structured" ? "active" : ""}
          onClick={() => {
            if (rootEditable) {
              setMode("structured");
            }
          }}
          title={rootEditable ? "Edit as key/value fields" : "Only objects and lists can be edited as fields"}
          style={rootEditable ? {} : { opacity: 0.5, cursor: "not-allowed" }}
        >
          <Icon icon="mdi:table" /> Fields
        </ModeButton>
        <ModeButton className={mode === "json" ? "active" : ""} onClick={() => setMode("json")}>
          <Icon icon="mdi:code-json" /> JSON
        </ModeButton>

        {mode === "json" && (
          <PlainButton
            onClick={() => {
              if (parsed.ok) {
                setJsonText(formatMetadata(parsed.value));
                setJsonError(null);
              }
            }}
          >
            <Icon icon="mdi:format-align-left" /> Format
          </PlainButton>
        )}

        <Spacer />

        <Hint>
          {getValueKind(parsed.value) === "object" ? `${entryCount} key(s)` : `${entryCount} item(s)`}
          {" · "}
          {parsed.empty ? "not set" : "set"}
        </Hint>
      </Toolbar>

      {!rootEditable && (
        <Hint>
          The root of this metadata is a {getValueKind(parsed.value)}, so it can only be edited as
          JSON.
        </Hint>
      )}

      {mode === "json" || !rootEditable ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <JsonBox
            value={jsonText}
            spellCheck={false}
            onChange={(event) => commitJson(event.target.value)}
          />
          {jsonError ? (
            <ErrorBanner>Not applied — {jsonError}</ErrorBanner>
          ) : (
            <Hint>Changes apply as soon as the JSON is valid.</Hint>
          )}
        </div>
      ) : (
        <MetadataTree value={parsed.value} onChange={(next) => onChange(serializeMetadata(next))} />
      )}
    </div>
  );
};

export default MetadataEditor;
